import type { RJSFSchema, UiSchema } from '@rjsf/utils';
import type { ReactElement } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import ErrorState from 'app/shared-components/common/ErrorState';

import { broadcastPropsUpdated } from 'app/pages/PlusWebsites/components/preview-messages';

import { WebsiteSectionEditor } from '../../components/WebsiteSectionEditor';
import { useGetWebsiteSectionDetails } from './hooks/useGetWebsiteSectionDetails';
import { useUpdateWebsiteSection } from './hooks/useUpdateWebsiteSection';

const EditWebsiteSectionPage = (): ReactElement => {
  const { sectionId = '' } = useParams<{ sectionId: string }>();
  const [searchParams] = useSearchParams();
  const websiteId = searchParams.get('websiteId') ?? '';

  const { sectionDetails, isLoading, isError, error } = useGetWebsiteSectionDetails({
    websiteId,
    sectionId,
  });

  const { mutateAsync: updateWebsiteSectionAsync, isPending } = useUpdateWebsiteSection();

  if (!websiteId || !sectionId) {
    return (
      <ErrorState
        title="Missing required parameters"
        subtitle="Cannot edit section without valid website and section information"
      />
    );
  }

  if (isError) {
    return (
      <ErrorState
        title="Failed to load section"
        subtitle={error instanceof Error ? error.message : 'Try again later'}
      />
    );
  }

  // Returns the promise so the editor can await the save before clearing its unsaved state.
  const handleSubmit = (data: Record<string, unknown>) =>
    updateWebsiteSectionAsync(
      { websiteId, sectionId, data },
      { onSuccess: () => broadcastPropsUpdated() }
    );

  return (
    <WebsiteSectionEditor
      title={`Edit ${sectionDetails?.sectionType.displayName ?? ''}`}
      schema={(sectionDetails?.sectionType.jsonSchema ?? {}) as RJSFSchema}
      uiSchema={(sectionDetails?.sectionType.uiSchema ?? {}) as UiSchema}
      formData={sectionDetails?.data}
      isLoading={isLoading}
      isSaving={isPending}
      onSubmit={handleSubmit}
    />
  );
};

export default EditWebsiteSectionPage;
